/**
 * Data transformation utilities for feedback operations
 * Converts UI form data to API-compatible format
 */

import { FeedbackFormData, FeedbackType } from '../features/chat/components/UserFeedbackModal/types';
import { FeedbackRequest } from '../api/feedback';
import { getPrimaryGenieSpaceName } from './genie';

/**
 * Extended message interface for feedback context
 */
export interface MessageForFeedback {
  id: string;
  content: string;
  role: 'user' | 'assistant' | 'system';
  parentMessage?: {
    content: string;
    role: 'user' | 'assistant' | 'system';
  };
  metadata?: {
    originalMessageId?: string;
    [key: string]: any;
  };
  genie_space_names?: string[];
  genie_conversation_id?: string;
  genie_message_id?: string;
}

/**
 * Get app version from environment with fallback
 */
const getAppVersion = (): string => {
  return import.meta.env.VITE_APP_VERSION || '1.0.0';
};

/**
 * Transform feedback form data to API request format
 *
 * @param formData - Form data from UserFeedbackModal
 * @param feedbackType - Positive or negative feedback
 * @param message - Message being rated
 * @param conversationId - Current conversation ID
 * @param userName - Optional user identifier
 * @param userQuery - Optional user query (for context)
 * @returns API-compatible feedback request
 */
export function transformFeedbackData(
  formData: FeedbackFormData,
  feedbackType: FeedbackType,
  message: MessageForFeedback,
  conversationId: string,
  userName?: string,
  userQuery?: string
): FeedbackRequest {
  const now = new Date().toISOString();

  // Use the original message ID if available, fallback to current ID
  const responseId = message.metadata?.originalMessageId || message.id;

  // Build base request with required fields
  const baseRequest: FeedbackRequest = {
    responseID: responseId,
    feedbackType,
    sharePrompt: formData.shareData ? 'yes' : 'no',
    feedbackSubmissionTime: now,
    appVersion: getAppVersion(),
    conversationID: conversationId,
  };

  // Add optional comment if provided
  if (formData.details?.trim()) {
    baseRequest.comment = formData.details.trim();
  }

  // Add issue category for negative feedback
  if (feedbackType === 'negative' && formData.issue?.trim()) {
    baseRequest.issue = formData.issue.trim();
  }

  // Add user name if provided
  if (userName?.trim()) {
    baseRequest.userName = userName.trim();
  }

  // Include query and answer only if user consents to data sharing
  if (formData.shareData) {
    // Use provided user query, or try to get from parent message
    if (userQuery?.trim()) {
      baseRequest.query = userQuery.trim();
    } else if (message.parentMessage?.role === 'user') {
      baseRequest.query = message.parentMessage.content;
    }

    // Include the assistant's response
    if (message.role === 'assistant') {
      baseRequest.answer = message.content;
    }
  }

  // include genie data
  const genieSpaceName = getPrimaryGenieSpaceName(message.genie_space_names);
  if (genieSpaceName && message.genie_conversation_id && message.genie_message_id) {
    baseRequest.genie_space_name = genieSpaceName;
    baseRequest.genie_conversation_id = message.genie_conversation_id;
    baseRequest.genie_message_id = message.genie_message_id;
  }

  return baseRequest;
}

/**
 * Validate feedback form data before submission
 *
 * @param formData - Form data to validate
 * @param feedbackType - Type of feedback being submitted
 * @returns Validation result with errors if any
 */
export function validateFeedbackData(
  formData: FeedbackFormData,
  feedbackType: FeedbackType
): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  // For negative feedback, require both issue and details
  if (feedbackType === 'negative') {
    const hasIssue = formData.issue?.trim();
    const hasDetails = formData.details?.trim();

    if (!hasIssue) {
      errors.push('Please select an issue category for negative feedback');
    }

    if (!hasDetails) {
      errors.push('Please provide details for negative feedback');
    }
  }

  // Validate details length if provided
  if (formData.details?.trim() && formData.details.trim().length > 1000) {
    errors.push('Feedback details must be less than 1000 characters');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Create a quick feedback request for simple thumbs up/down actions
 *
 * @param messageId - ID of message being rated
 * @param feedbackType - Positive or negative
 * @param conversationId - Current conversation ID
 * @param shareData - Whether to share data with team
 * @param comment - Optional comment
 * @param genieMetadata - Optional Genie metadata for Genie agent responses
 * @returns Minimal feedback request
 */
export function createQuickFeedbackRequest(
  messageId: string,
  feedbackType: FeedbackType,
  conversationId: string,
  shareData: boolean = false,
  comment?: string,
  genieMetadata?: {
    genie_space_names?: string[];
    genie_conversation_id?: string;
    genie_message_id?: string;
  }
): FeedbackRequest {
  const request: FeedbackRequest = {
    responseID: messageId,
    feedbackType,
    sharePrompt: shareData ? 'yes' : 'no',
    feedbackSubmissionTime: new Date().toISOString(),
    appVersion: getAppVersion(),
    conversationID: conversationId,
  };

  if (comment?.trim()) {
    request.comment = comment.trim();
  }

  // include genie metadata
  if (genieMetadata) {
    const genieSpaceName = getPrimaryGenieSpaceName(genieMetadata.genie_space_names);
    if (genieSpaceName && genieMetadata.genie_conversation_id && genieMetadata.genie_message_id) {
      request.genie_space_name = genieSpaceName;
      request.genie_conversation_id = genieMetadata.genie_conversation_id;
      request.genie_message_id = genieMetadata.genie_message_id;
    }
  }

  return request;
}

/**
 * Extract user query from conversation context
 * Looks for the user message that prompted the assistant response
 *
 * @param message - Assistant message being rated
 * @param conversationHistory - Full conversation history
 * @returns User query that prompted this response, if found
 */
export function extractUserQuery(
  message: MessageForFeedback,
  conversationHistory?: any[]
): string | undefined {
  // First try parent message if available
  if (message.parentMessage?.role === 'user') {
    return message.parentMessage.content;
  }

  // If no conversation history, can't find query
  if (!conversationHistory || !Array.isArray(conversationHistory)) {
    return undefined;
  }

  // Find this message in conversation history and get preceding user message
  const messageIndex = conversationHistory.findIndex(
    msg => msg.message_id === (message.metadata?.originalMessageId || message.id)
  );

  if (messageIndex > 0) {
    const previousMessage = conversationHistory[messageIndex - 1];
    if (previousMessage?.user) {
      return previousMessage.user;
    }
  }

  return undefined;
}

/**
 * Sanitize feedback content to remove sensitive information
 *
 * @param content - Content to sanitize
 * @returns Sanitized content
 */
export function sanitizeFeedbackContent(content: string): string {
  if (!content?.trim()) {
    return '';
  }

  // Remove potential sensitive patterns (email, phone, etc.)
  // This is a basic implementation - could be enhanced with more sophisticated patterns
  return content
    .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL]')
    .replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, '[PHONE]')
    .replace(/\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g, '[CARD]')
    .trim();
}

/**
 * Feedback transformation utilities namespace
 */
export const feedbackTransformUtils = {
  transformFeedbackData,
  validateFeedbackData,
  createQuickFeedbackRequest,
  extractUserQuery,
  sanitizeFeedbackContent,
  getAppVersion,
};
